import { useEffect, useState } from "react";
import { Settings2, ChevronUp, ChevronDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  ATTENTION_FILTER_LABELS,
  type ConversationAttentionFilter,
} from "@/lib/conversation-filters";
import { cn } from "@/lib/utils";

interface ConversationInboxLayoutSettingsProps {
  tabOrder: ConversationAttentionFilter[];
  onSave: (order: ConversationAttentionFilter[]) => Promise<boolean>;
}

export function ConversationInboxLayoutSettings({
  tabOrder,
  onSave,
}: ConversationInboxLayoutSettingsProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<ConversationAttentionFilter[]>(tabOrder);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft([...tabOrder]);
  }, [open, tabOrder]);

  const move = (index: number, direction: -1 | 1) => {
    const next = [...draft];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setDraft(next);
  };

  const handleSave = async () => {
    setSaving(true);
    const ok = await onSave(draft);
    setSaving(false);
    if (ok) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          title="Configurar ordem das abas"
        >
          <Settings2 className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 p-3">
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium">Ordem das abas</p>
            <p className="text-xs text-muted-foreground mt-1">
              A primeira da lista é a aba que abre ao entrar em Conversas. Salvo só para você.
            </p>
          </div>

          <ul className="space-y-1">
            {draft.map((tab, index) => (
              <li
                key={tab}
                className={cn(
                  "flex items-center gap-2 rounded-md border px-2 py-1.5",
                  index === 0 && "border-primary/40 bg-primary/5"
                )}
              >
                <div className="flex flex-col gap-0.5">
                  <button
                    type="button"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                    className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30"
                    aria-label="Mover para cima"
                  >
                    <ChevronUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={index === draft.length - 1}
                    onClick={() => move(index, 1)}
                    className="h-5 w-5 flex items-center justify-center rounded hover:bg-muted disabled:opacity-30"
                    aria-label="Mover para baixo"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">{ATTENTION_FILTER_LABELS[tab].label}</p>
                  {index === 0 && (
                    <p className="text-[10px] text-primary">Abre ao entrar</p>
                  )}
                </div>
              </li>
            ))}
          </ul>

          <Button type="button" size="sm" className="w-full" disabled={saving} onClick={handleSave}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salvar preferência"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
